/* eslint-disable complexity */
import { Component } from 'react'
import { reduxForm, reset } from 'redux-form'
import { Col, Row } from 'react-bootstrap'
import { StyledSubHeading } from 'components/headings'
import { Checkbox, Form, Input } from 'components/forms'
import { LinkButton, SaveButton, InfoButton } from 'components/buttons'
import NameFieldset from 'registry/forms/name-fieldset'
import { saveUser, deleteTwoFactor, getTwoFactors } from 'registry/actions'
import withCurrentUser from 'shared/HOC/withCurrentUser'
import { forgotPasswordPath } from 'lib/urls'
import {
  createValidator,
  email,
  MSG_EMAIL_REQUIRED,
  required,
} from 'lib/form-validation'
import { ConfigureTwoFactorAuthModal } from '../../components/modals/ConfigureTwoFactorAuthModal'
import DeactivateAccountModal from '../../components/modals/deactivate-account-modal'
import css from './account-info.scss'

const fieldValidator = createValidator({
  'user.firstName': [required],
  'user.lastName': [required],
  'user.email': [{ rule: required, msg: MSG_EMAIL_REQUIRED }, email],
})

const validate = (data) => {
  // Display error if user inputted verify password with no new password
  if (!data.user.password && data.user.verifyPassword) {
    data.user.password = ''
    // Set values to undefined if nothing has changed
  } else if (!data.user.password && !data.user.verifyPassword) {
    data.user.password = undefined
    data.user.verifyPassword = undefined
  }

  if (data.user.password !== data.user.verifyPassword) {
    return {
      user: {
        password: 'passwords do not match',
        verifyPassword: 'passwords do not match',
      },
    }
  }
  return fieldValidator(data)
}

class AccountInfo extends Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  openAdd2FAModal = () => {
    this.setState({ showAdd2FAModal: true })
  }

  closeAdd2FAModal = () => {
    this.setState({ showAdd2FAModal: false })
  }

  openDeactivateAccountModal = () =>
    this.setState({ showDeactivateAccountModal: true })

  closeDeactivateAccountModal = () =>
    this.setState({ showDeactivateAccountModal: false }, this.props.resetForm)

  handleDeleteTwoFactor = (id) => {
    this.props.deleteTwoFactor(id)
  }

  handleSubmit = (data, dispatch) => {
    const { currentUser, setCurrentUser } = this.props

    setCurrentUser({
      ...currentUser,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      email: data.user.email,
    })

    dispatch(saveUser(data)).then(() => {
      PubSub.publish(BLConstants.FLASH_MESSAGE, {
        message: 'Your account info has been updated!',
        variant: 'success',
      })
    })
  }

  componentDidMount() {
    this.props.getTwoFactors()
  }

  render() {
    const {
      fields: { user },
      handleSubmit,
      isAdmin,
      twoFactors,
      isBLDMECustomer,
      currentUser,
    } = this.props
    const submitter = handleSubmit(this.handleSubmit)

    const ssoAccount = currentUser?.userLoginAccounts?.find(
      (account) => account.provider !== 'password'
    )

    let message = ''

    if (isBLDMECustomer) {
      message = (
        <span>
          Our records show you have either initiated or placed a Babylist Health
          order. To change your email address across your registry and your
          Babylist Health order, our Customer Support team needs to further
          verify your account. Please email{' '}
          <a href="mailto:support@babylist.com">support@babylist.com</a> to
          request an email change.
        </span>
      )
    } else if (ssoAccount) {
      message = (
        <span>
          Your account is linked to{' '}
          {ssoAccount.provider.charAt(0).toUpperCase() +
            ssoAccount.provider.slice(1)}
          . If you would like to change your email, please reach out to support
          by emailing{' '}
          <a
            className={css.supportDecorator}
            href="mailto:support@babylist.com"
          >
            support@babylist.com
          </a>
        </span>
      )
    }

    return (
      <div>
        <ConfigureTwoFactorAuthModal
          show={this.state.showAdd2FAModal}
          onHide={this.closeAdd2FAModal}
        />

        <DeactivateAccountModal
          show={this.state.showDeactivateAccountModal}
          onHide={this.closeDeactivateAccountModal}
        />

        <StyledSubHeading>Account Info</StyledSubHeading>
        <Form onSubmit={submitter}>
          <NameFieldset firstName={user.firstName} lastName={user.lastName} />
          <Row>
            <Col sm={6}>
              <Input
                disabled={isBLDMECustomer || ssoAccount}
                help={message}
                label="Email Address"
                id="email_address"
                {...user.email}
                error={user.email.touched ? user.email.error : null}
              />
            </Col>
          </Row>

          <br />

          <StyledSubHeading className="h5">Change Password</StyledSubHeading>
          <Row>
            <Col sm={6}>
              <Input
                groupStyle={{ marginBottom: '4px' }}
                id="current_password"
                label="Current Password"
                type="password"
                {...user.currentPassword}
                error={user.currentPassword.error}
              />
              <a className={css.forgotPassword} href={forgotPasswordPath}>
                Forgot your password?
              </a>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Input
                label="New Password"
                id="new_password"
                type="password"
                {...user.password}
                error={
                  user.password.touched && user.verifyPassword.touched
                    ? user.password.error
                    : null
                }
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Input
                label="Verify New Password"
                id="verify_new_password"
                type="password"
                {...user.verifyPassword}
                error={
                  user.verifyPassword.touched ? user.verifyPassword.error : null
                }
              />
            </Col>
          </Row>

          {isAdmin && (
            <>
              <br />
              <StyledSubHeading className="h5">
                Two-factor Authentication
              </StyledSubHeading>
              <Row>
                <Col sm={12}>
                  {twoFactors && twoFactors.length > 0 ? (
                    <div className="h6">
                      {twoFactors.map((twoFactor) => (
                        <p className="flex" key={twoFactor.id}>
                          <span className="text-bold">Authenticator App</span>
                          <LinkButton
                            className="man mll pan"
                            onClick={() =>
                              this.handleDeleteTwoFactor(twoFactor.id)
                            }
                          >
                            Disable
                          </LinkButton>
                        </p>
                      ))}
                    </div>
                  ) : (
                    <InfoButton pill onClick={this.openAdd2FAModal}>
                      Configure
                    </InfoButton>
                  )}
                </Col>
              </Row>
            </>
          )}

          <br />

          <StyledSubHeading className="h5">Deactivate Account</StyledSubHeading>
          <button
            className={css.accountDeactivate}
            type="button"
            onClick={() => this.openDeactivateAccountModal()}
          >
            Deactivate My Account
          </button>
          <SaveButton pill submitting={this.props.submitting} />
        </Form>
      </div>
    )
  }
}

const formName = 'accountInfo'
export default reduxForm(
  {
    form: formName,
    fields: [
      'user.firstName',
      'user.lastName',
      'user.email',
      'user.password',
      'user.currentPassword',
      'user.verifyPassword',
    ],
    validate,
  },
  (state, ownProps) => ({
    initialValues: {
      user: {
        firstName: state.user.firstName,
        lastName: state.user.lastName,
        email: state.user.email,
      },
    },
    isAdmin: state.user.admin,
    twoFactors: state.user.twoFactors,
    isBLDMECustomer: state.user.isBLDMECustomer,
    onSubmitFail: (errors) => {
      if (errors && errors._error) {
        PubSub.publish(BLConstants.FLASH_MESSAGE, {
          message: BLConstants.GENERIC_FAILURE_MESSAGE,
          variant: 'danger',
        })
      }
    },
  }),
  (dispatch) => ({
    resetForm: () => dispatch(reset(formName)),
    getTwoFactors: () => dispatch(getTwoFactors()),
    deleteTwoFactor: (id) => dispatch(deleteTwoFactor(id)),
  })
)(withCurrentUser(AccountInfo))
